import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

export type PaymentStatus =
  | "pending"
  | "paid"
  | "cash_expected"
  | "failed"
  | "cancelled"
  | "";

export type OrderStatus =
  | "pending"
  | "paid"
  | "shipped"
  | "delivered"
  | "cancelled"
  | "completed"
  | "returned"
  | string;

export type CheckoutMode = "delivery" | "pickup" | "";

export type PricingBreakdown = {
  currency?: string;

  productSubtotal?: number;
  serviceFee?: number;
  deliveryFee?: number;
  discountAmount?: number;
  gatewayFee?: number;

  total?: number;

  sellerCommissionTotal?: number;
  sellerNetPayableTotal?: number;

  driverEarningTotal?: number;
  deliveryMargin?: number;
  platformNetRevenue?: number;

  deliveryDistanceKm?: number;
  totalActualWeightKg?: number;
  totalVolumetricWeightKg?: number;
  billableWeightKg?: number;
  bulkySurcharge?: number;
};

export type OrderItem = {
  _id?: string;
  productId?: string;
  sellerUserId?: string;
  ownerId?: string;
  dispatchWarehouseId?: string;
  sellerBusinessName?: string;

  title: string;
  description?: string;
  category?: string;
  imageUrl?: string;
  price: number;
  quantity: number;

  selectedVariants?: any[];
  variantSummary?: string;

  isPhysical?: boolean;
  weightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  bulky?: boolean;
};

export type OrderLocation = {
  lat?: number;
  lng?: number;
};

export type OrderPayment = {
  method?: string;
  provider?: string;
  status?: PaymentStatus;
  providerPaymentId?: string;
  providerCheckoutId?: string;
  reference?: string;

  cardNumber?: string;
  cardHolder?: string;
  expiryDate?: string;
  cvv?: string;

  mobileProvider?: string;
  mobileNumber?: string;
};

export type Order = {
  _id: string;
  items: OrderItem[];

  total: number;
  currency?: string;

  userId?: string;
  ownerUserId?: string;
  sellerUserId?: string;
  sellerName?: string;

  checkoutGroupId?: string;
  checkoutGroupOrderCount?: number;
  fulfilmentOrderNumber?: string;

  email?: string;
  phoneNumber?: string;
  name?: string;

  fromAddress?: string;
  toAddress?: string;
  fromLocation?: OrderLocation;
  toLocation?: OrderLocation;

  deliveryInstructions?: string;

  pickupAddress?: string;
  pickupWarehouseId?: string;
  pickupWarehouseName?: string;
  pickupWarehouseAddress?: string;
  dispatchWarehouseId?: string;

  checkoutMode?: CheckoutMode;
  category?: string;
  status?: OrderStatus;

  chargeId?: string;
  reference?: string;
  paymentVerifiedAt?: string;

  payment?: OrderPayment;
  pricingBreakdown?: PricingBreakdown;

  deliveryTracking?: {
    waybillId?: string;
    waybillNumber?: string;
    routeBatchId?: string;
    dispatchJobId?: string;
    assignedDriverId?: string;
    status?: string;
    assignedAt?: string;
    arrivedAtPickupAt?: string;
    pickedUpAt?: string;
    inTransitAt?: string;
    arrivedAtDeliveryAt?: string;
    deliveryFailedAt?: string;
    returnStartedAt?: string;
    returnedAt?: string;
    deliveredAt?: string;
    cancelledAt?: string;
    failureReason?: string;
    returnReason?: string;
  };

  createdAt?: string;
  updatedAt?: string;
};

export type CreateOrderPayload = Omit<Order, "_id"> & {
  total: number;
};

export type SellerGroupedOrderPayload = {
  sellerUserId: string;
  ownerUserId?: string;
  sellerName?: string;

  items: OrderItem[];

  total?: number;
  currency?: string;

  fromAddress: string;
  toAddress: string;
  fromLocation?: OrderLocation;
  toLocation?: OrderLocation;

  deliveryInstructions?: string;

  pickupAddress?: string;
  pickupWarehouseId: string;
  pickupWarehouseName?: string;
  pickupWarehouseAddress?: string;
  dispatchWarehouseId?: string;

  checkoutMode: CheckoutMode;
  category?: "product";

  deliveryFee?: number;
  distanceKm?: number;
  discountAmount?: number;

  pricingBreakdown?: PricingBreakdown;
};

export type CreateGroupedCheckoutPayload = {
  checkoutGroupId?: string;
  userId: string;
  email: string;
  phoneNumber?: string;
  name?: string;

  payment?: OrderPayment;
  checkoutMode: CheckoutMode;

  chargeId?: string;
  reference?: string;

  sellerOrders: SellerGroupedOrderPayload[];
};

export type GroupedCheckoutResponse = {
  checkoutGroupId: string;
  orderCount: number;
  total: number;
  primaryOrderId: string;
  orders: Order[];
};

export type OrderTrackingResponse = {
  _id: string;
  checkoutGroupId?: string;
  status: string;
  total: number;
  address?: string;
  createdAt?: string;
  updatedAt?: string;
  orders?: Order[];
  waybills?: any[];
};

type ApiResponse<T> = {
  success?: boolean;
  data: T;
  message?: string;
};

export const orderApi = createApi({
  reducerPath: "orderApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_BASE_URL}/api/v1`,
  }),
  tagTypes: ["Orders", "OrderTracking"],
  endpoints: (builder) => ({
    createOrder: builder.mutation<Order, CreateOrderPayload>({
      query: (body) => ({
        url: "/orders",
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiResponse<Order>) => response.data,
      invalidatesTags: ["Orders"],
    }),

    createGroupedCheckoutOrder: builder.mutation<
      GroupedCheckoutResponse,
      CreateGroupedCheckoutPayload
    >({
      query: (body) => ({
        url: "/orders/grouped-checkout",
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiResponse<GroupedCheckoutResponse>) =>
        response.data,
      invalidatesTags: ["Orders", "OrderTracking"],
    }),

    getOrders: builder.query<Order[], void>({
      query: () => "/orders",
      transformResponse: (response: ApiResponse<Order[]>) => response.data,
      providesTags: ["Orders"],
    }),

    getOrderById: builder.query<Order, string>({
      query: (orderId) => `/orders/${orderId}`,
      transformResponse: (response: ApiResponse<Order>) => response.data,
      providesTags: (_result, _error, orderId) => [
        { type: "Orders", id: orderId },
      ],
    }),

    getOrdersByCheckoutGroup: builder.query<Order[], string>({
      query: (checkoutGroupId) => `/orders/checkout-group/${checkoutGroupId}`,
      transformResponse: (response: ApiResponse<Order[]>) => response.data,
      providesTags: ["Orders"],
    }),

    updateOrderStatus: builder.mutation<
      Order,
      { orderId: string; status: OrderStatus; userId?: string }
    >({
      query: ({ orderId, ...body }) => ({
        url: `/orders/${orderId}/status`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: ApiResponse<Order>) => response.data,
      invalidatesTags: ["Orders", "OrderTracking"],
    }),

    getOrderTracking: builder.query<OrderTrackingResponse, string>({
      query: (orderId) => `/orders/track/${orderId}`,
      transformResponse: (response: ApiResponse<OrderTrackingResponse>) =>
        response.data,
      providesTags: (_result, _error, orderId) => [
        { type: "OrderTracking", id: orderId },
      ],
    }),
  }),
});

export const {
  useCreateOrderMutation,
  useCreateGroupedCheckoutOrderMutation,
  useGetOrdersQuery,
  useGetOrderByIdQuery,
  useGetOrdersByCheckoutGroupQuery,
  useUpdateOrderStatusMutation,
  useGetOrderTrackingQuery,
} = orderApi;
